import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { HdBodygraphPlanetaryActivation, HdBodygraphRenderState, HdBodygraphSide, HdPlanetGridVisualizationMode } from '../../hd-bodygraph.models';
import { HdChartHoverHighlightService } from '../../hd-chart-hover-highlight.service';
import { HdContentLanguageService } from '../../hd-content-language.service';
import { HdPlanetGridColorConfig } from '../hd-planet-grid-color-config';
import { getHdPlanetDisplayConfig, HdPlanetDisplayConfig } from '../hd-planet-display-config';
import { findActivationFromList, isGateInDefinedCenter } from '../hd-planet-grid.utils';
import { HdPlanetActivationBlockClickEvent, HdPlanetActivationBlockComponent, HdPlanetGridDensity } from '../hd-planet-activation-block/hd-planet-activation-block.component';
import { HdColorToneBlockComponent, HdPhsBlockClickEvent } from '../hd-color-tone-block/hd-color-tone-block.component';
import { HdTransformationArrowComponent } from '../hd-transformation-arrow/hd-transformation-arrow.component';
import { PHS_CATEGORIES, PhsCategory, phsCategoryFor } from '../../hd-phs-metadata';
import { HdPhsInfoService } from '../../hd-phs-info.service';

interface HdLifeCrossBlockViewModel {
  activation: HdBodygraphPlanetaryActivation;
  displayConfig: HdPlanetDisplayConfig;
  isDefined: boolean;
}

/**
 * Groups North Node + South Node blocks with their View (Color/Tone) indicator.
 * For personality: [SN] [NN] [View]
 * For design (mirrored): [View] [NN] [SN]
 */
@Component({
  selector: 'hd-life-cross',
  templateUrl: './hd-life-cross.component.html',
  styleUrl: './hd-life-cross.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdPlanetActivationBlockComponent, HdColorToneBlockComponent, HdTransformationArrowComponent],
  host: {
    '[class.is-furnishings-dimmed]': 'furnishingsDimmed()',
  },
})
export class HdLifeCrossComponent {
  readonly activations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly side = input.required<HdBodygraphSide>();
  readonly renderState = input.required<HdBodygraphRenderState>();
  readonly colorConfig = input.required<HdPlanetGridColorConfig>();
  readonly showHdKeynotes = input(false);
  /** Classic (enkel cijfers) vs extended (PHS-iconen) weergave. */
  readonly visualizationMode = input<HdPlanetGridVisualizationMode>('extended');
  /** Mini-densiteit (mobiel): compacte twee-regel-blokken — zie {@link HdPlanetGridDensity}. */
  readonly density = input<HdPlanetGridDensity>('normal');
  /**
   * Map van `line` → LPS-gates op de **opposite** zijde, doorgegeven vanuit
   * de planet-grid. Wordt gebruikt om voor het link-role node-blok (PSN op
   * personality-zijde, DNN op design-zijde) de set van matching LPS-gates
   * af te leiden die bij hover over het link-icoontje mee oplichten.
   * Zie `docs/human-design/hd-chart-hover-highlight.md` (§ Link-node icon
   * hover).
   */
  readonly oppositeLpsLineToGates = input<ReadonlyMap<number, readonly number[]>>(new Map());
  /**
   * Per-planet (keyed by `planetId`) LPS gate that should highlight when
   * hovering the base badge of that planet's activation block. Resolved
   * upstream in `HdPlanetGridComponent` so cross-side lookups (link-node
   * crossover) stay in one place. See `.\docs\human-design\base-logic.md`.
   */
  readonly baseHoverTargetGates = input<ReadonlyMap<number, number>>(new Map());
  readonly blockClicked = output<HdPlanetActivationBlockClickEvent>();
  /**
   * Klik op het visie/omgeving color/tone-blok. Wordt door deze cross verrijkt
   * met de PHS-categorie + kleur/toon en doorgegeven aan de planet-grid voor
   * smart-placement van het info-panel.
   */
  readonly phsBlockClicked = output<HdPhsBlockClickEvent>();

  private readonly phsInfoService = inject(HdPhsInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });

  /**
   * Of de view kleur/toon-blok + transformatiepijl van de nodes moeten dimmen.
   * Tijdens een hover die niet de North of South Node van deze zijde betreft
   * worden de bijhorende "furnishings" (de view color/tone-blok en de
   * nodes-pijl) mee gedimd. Wanneer de hover wél matcht met NN of SN (zelfde
   * gate of een center waar NN/SN in zit) blijven ze helder.
   */
  protected readonly furnishingsDimmed = computed(() => {
    const service = this.hoverService;
    if (!service || !service.enabled() || !service.isActive()) return false;
    if (!service.dimsGrid()) return false;
    const nn = this.northNodeBlock();
    const sn = this.southNodeBlock();
    const nnMatch = nn != null && service.isGateMatchingHover(nn.activation.gate);
    const snMatch = sn != null && service.isGateMatchingHover(sn.activation.gate);
    return !(nnMatch || snMatch);
  });

  protected readonly northNodeConfig = getHdPlanetDisplayConfig('northNode');
  protected readonly southNodeConfig = getHdPlanetDisplayConfig('southNode');

  protected readonly northNodeBlock = computed<HdLifeCrossBlockViewModel | null>(() =>
    this.createBlockViewModel(this.northNodeConfig),
  );

  protected readonly southNodeBlock = computed<HdLifeCrossBlockViewModel | null>(() =>
    this.createBlockViewModel(this.southNodeConfig),
  );

  protected readonly viewColor = computed<number | null>(() =>
    this.northNodeBlock()?.activation.color ?? null,
  );

  protected readonly viewTone = computed<number | null>(() =>
    this.northNodeBlock()?.activation.tone ?? null,
  );

  /** PHS-categorie van deze life-cross: design → omgeving, personality → visie. */
  protected readonly phsCategory = computed<PhsCategory | null>(() => {
    const side = this.side();
    return side === 'design' || side === 'personality' ? phsCategoryFor(side, 'life') : null;
  });

  private readonly phsCategoryInfo = computed(() => {
    const category = this.phsCategory();
    if (!category) return null;
    return this.phsInfoService.categoryInfoIn(category, this.contentLang.effectiveLang());
  });

  /** Verrijkt de child-klik met categorie/kleur/toon en bubblet naar de planet-grid. */
  protected onPhsBlockClicked(event: { origin: { x: number; y: number } }): void {
    const category = this.phsCategory();
    const color = this.viewColor();
    const tone = this.viewTone();
    const side = this.side();
    if (!category || color == null || tone == null) return;
    this.phsBlockClicked.emit({
      category,
      side,
      color,
      tone,
      colorConfig: this.colorConfig(),
      origin: event.origin,
    });
  }

  /** Kleur-icoon (kale FA-naam) voor het kleur/toon-blok in extended-modus. */
  protected readonly phsColorIcon = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const category = this.phsCategory();
    const color = this.viewColor();
    if (!category || color == null) return null;
    return PHS_CATEGORIES[category].colors[color - 1]?.icon ?? null;
  });

  /** Cognitie-icoon (kale FA-naam) op basis van de toon, in extended-modus. */
  protected readonly phsCognitionIcon = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const category = this.phsCategory();
    const tone = this.viewTone();
    if (!category || tone == null || tone < 1 || tone > 6) return null;
    return PHS_CATEGORIES[category].cognitionIcons[tone - 1] ?? null;
  });

  /** Hover-tooltip (keynote) voor het kleur-icoon, in extended-modus. */
  protected readonly phsColorTooltip = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const color = this.viewColor();
    if (color == null || color < 1 || color > 6) return null;
    const info = this.phsCategoryInfo()?.colors[color - 1];
    if (!info) return null;
    return info.short ? `${info.name} — ${info.short}` : info.name;
  });

  /** Hover-tooltip (keynote) voor het cognitie-icoon, in extended-modus. */
  protected readonly phsCognitionTooltip = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const tone = this.viewTone();
    if (tone == null || tone < 1 || tone > 6) return null;
    const cognition = this.phsCategoryInfo()?.cognition[tone - 1];
    if (!cognition) return null;
    return cognition.short ? `${cognition.name} — ${cognition.short}` : cognition.name;
  });

  /** Icoon van de actieve toon-variant (links 1-3 / rechts 4-6) van de kleur. */
  protected readonly phsVariantIcon = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const category = this.phsCategory();
    const color = this.viewColor();
    const tone = this.viewTone();
    if (!category || color == null || color < 1 || color > 6 || tone == null || tone < 1 || tone > 6) return null;
    const colorMeta = PHS_CATEGORIES[category].colors[color - 1];
    if (!colorMeta) return null;
    return tone <= 3 ? colorMeta.toneLeftIcon : colorMeta.toneRightIcon;
  });

  /** Hover-tooltip (keynote) voor het toon-variant-icoon, in extended-modus. */
  protected readonly phsVariantTooltip = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const color = this.viewColor();
    const tone = this.viewTone();
    if (color == null || color < 1 || color > 6 || tone == null || tone < 1 || tone > 6) return null;
    const info = this.phsCategoryInfo()?.colors[color - 1];
    if (!info) return null;
    const variant = tone <= 3 ? info.tone.left : info.tone.right;
    return variant.short ? `${variant.name} — ${variant.short}` : variant.name;
  });

  /** Naam van de actieve toon-variant (kalm/nerveus, …) — voor de keynotes-modus. */
  protected readonly phsVariantName = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const color = this.viewColor();
    const tone = this.viewTone();
    if (color == null || color < 1 || color > 6 || tone == null || tone < 1 || tone > 6) return null;
    const info = this.phsCategoryInfo()?.colors[color - 1];
    if (!info) return null;
    return tone <= 3 ? info.tone.left.name : info.tone.right.name;
  });

  /** Cognitie-naam o.b.v. de toon — voor de keynotes-modus. */
  protected readonly phsCognitionName = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const tone = this.viewTone();
    if (tone == null || tone < 1 || tone > 6) return null;
    return this.phsCategoryInfo()?.cognition[tone - 1]?.name ?? null;
  });

  protected readonly isMirrored = computed(() => this.side() === 'design');

  /**
   * De transformatiepijl hangt absoluut náást het planet-grid-paneel; in
   * mini-densiteit (twee grids naast elkaar op een telefoon) is daar geen
   * ruimte voor, dus vervalt ze daar. De richting-informatie blijft afleidbaar
   * uit het toon-cijfer in het kleur/toon-blok.
   */
  protected readonly showTransformationArrow = computed(() =>
    this.density() === 'normal' && this.viewTone() !== null,
  );

  /**
   * Poorten waar de view kleur/toon-blok + nodes-pijl bij horen (North Node
   * + South Node van deze zijde). Wordt aan de furnishing-componenten
   * doorgegeven zodat hover op die elementen dezelfde chart-highlight
   * triggert als een hover op deze poorten zelf.
   */
  /**
   * Matching opposite-side LPS gates voor het link-role node-blok:
   *  - personality-zijde → southNode is `link` → match-gates afgeleid uit
   *    SN.line in de design-LPS line→gates map.
   *  - design-zijde → northNode is `link` → match-gates afgeleid uit NN.line
   *    in de personality-LPS line→gates map.
   * Het andere node-blok krijgt een lege lijst (rol = `prime` → geen
   * link-icon hover-uitbreiding).
   */
  protected readonly southNodeLinkMatchGates = computed<readonly number[]>(() => {
    if (this.side() !== 'personality') return [];
    const sn = this.southNodeBlock();
    if (!sn) return [];
    return this.oppositeLpsLineToGates().get(sn.activation.line) ?? [];
  });

  protected readonly northNodeLinkMatchGates = computed<readonly number[]>(() => {
    if (this.side() !== 'design') return [];
    const nn = this.northNodeBlock();
    if (!nn) return [];
    return this.oppositeLpsLineToGates().get(nn.activation.line) ?? [];
  });

  protected readonly northNodeBaseHoverGate = computed<number | null>(() => {
    const block = this.northNodeBlock();
    if (!block) return null;
    return this.baseHoverTargetGates().get(block.activation.planetId) ?? null;
  });

  protected readonly southNodeBaseHoverGate = computed<number | null>(() => {
    const block = this.southNodeBlock();
    if (!block) return null;
    return this.baseHoverTargetGates().get(block.activation.planetId) ?? null;
  });

  protected readonly furnishingGates = computed<readonly number[]>(() => {
    const gates: number[] = [];
    const nn = this.northNodeBlock();
    if (nn) gates.push(nn.activation.gate);
    const sn = this.southNodeBlock();
    if (sn) gates.push(sn.activation.gate);
    return gates;
  });

  private createBlockViewModel(config: HdPlanetDisplayConfig): HdLifeCrossBlockViewModel | null {
    const activation = findActivationFromList(this.activations(), this.side(), config.planetId);

    if (!activation) {
      return null;
    }

    return {
      activation,
      displayConfig: config,
      isDefined: isGateInDefinedCenter(activation.gate, this.renderState()),
    };
  }
}
